import { useEffect, useRef, useState } from "react";
import { BULLET } from "@/constants";
import { Text } from "@/ui-kits";

interface BulletTextInputProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  headerText?: string;
}

export function BulletTextInput({
  value,
  onChange,
  placeholder = "Start typing…",
  headerText = "Please upload the following reports:",
}: BulletTextInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [text, setText] = useState<string>(value ?? BULLET);

  useEffect(() => {
    if (value !== undefined) {
      setText(value || BULLET);
    }
  }, [value]);

  const autoGrow = () => {
    const el = textareaRef.current;
    if (!el) return;

    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };

  useEffect(() => {
    autoGrow();
  }, [text]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setText(val);
    onChange?.(val);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Prevent deletion of bullet points with backspace
    if (e.key === "Backspace") {
      const el = textareaRef.current;
      if (!el) return;

      const start = el.selectionStart ?? 0;
      const end = el.selectionEnd ?? 0;

      // If there's a selection, allow normal backspace
      if (start !== end) return;

      // Check if cursor is right after a bullet
      const beforeCursor = text.slice(Math.max(0, start - BULLET.length), start);
      
      if (beforeCursor === BULLET) {
        e.preventDefault();
        return;
      }

      // Check if we're at the start of a line with a bullet
      const textBeforeCursor = text.slice(0, start);
      const lastNewlineIndex = textBeforeCursor.lastIndexOf("\n");
      const currentLineStart = lastNewlineIndex + 1;
      const currentLine = text.slice(currentLineStart, start);

      // If the current line only has the bullet and cursor is right after it
      if (currentLine.trim() === BULLET.trim() && start === currentLineStart + BULLET.length) {
        e.preventDefault();
        return;
      }
    }

    if (e.key !== "Enter") return;

    e.preventDefault();
    e.stopPropagation();

    const el = textareaRef.current;
    if (!el) return;

    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;

    const updated =
      text.slice(0, start) +
      "\n" +
      BULLET +
      text.slice(end);

    setText(updated);
    onChange?.(updated);

    requestAnimationFrame(() => {
      el.selectionStart = el.selectionEnd =
        start + 1 + BULLET.length;
    });
  };

  return (
    <div style={{ width: "100%", position: "relative" }}>
      {/* Non-editable header */}
      <Text className="px-1 mt-1">
        {headerText}
      </Text>

      {/* Editable textarea */}
      <textarea
        ref={textareaRef}
        value={text}
        placeholder={placeholder}
        rows={1}
        onChange={handleChange as unknown as React.ChangeEventHandler<HTMLTextAreaElement>}
        onKeyDown={handleKeyDown}
        style={{
          width: "100%",
          resize: "none",
          overflow: "hidden",
          fontSize: "16px",
          lineHeight: "1.5",
          padding: "4px 12px 10px 12px",
          boxSizing: "border-box",
          backgroundColor: "transparent",
          border: "none",
          outline: "none",
        }}
      />
    </div>
  );
}